<template>
    <div
        class="info-tooltip absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-200 transition-opacity duration-150 pointer-events-none p-4 text-left rounded top-10 right-0 z-50 whitespace-nowrap text-[11px]">
        <div v-if="hasMain" class="mb-2 text-darkgray">Основной табель: {{ mainTotal }}</div>
        <div v-if="hasAdditional" class="mb-2 text-darkgray">Дополнительный табель: {{ additionalTotal }}</div>
        <div v-if="nightMainTotal" class="text-darkgray mb-2">Основных ночных часов: {{ nightMainTotal }}</div>
        <div v-if="nightAdditionalTotal" class="text-darkgray mb-2">Дополнительных ночных часов: {{ nightAdditionalTotal }}</div>
        <div v-if="nightTotal" class="text-darkgray mb-2">Всего ночных часов: {{ nightTotal }}</div>
        <div class="text-darkgray mb-2">Всего рабочих часов: {{ factTotal }}</div>

        <div class="border-b border-graylight my-2"></div>

        <div v-for="(shift, index) in sortedShifts" :key="shift.id || index">
            <div class="mb-2 text-gray text-[10px]">{{ getShiftPosition(shift) }}:
                <span v-if="getShiftStart(shift)">{{ formatShiftTime(getShiftStart(shift)) }}</span>
                <span v-else>??:??</span>
                -
                <span v-if="getShiftEnd(shift)">{{ formatShiftTime(getShiftEnd(shift)) }}</span>
                <span v-else>??:??</span>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import dayjs from 'dayjs'

type TimeValue = string | number

interface Props {
    fact?: TimeValue,
    night?: TimeValue,
    nightMain?: TimeValue,
    nightAdditional?: TimeValue,
    main?: TimeValue,
    additional?: TimeValue,
    schedule?: any,
    day?: string | number,
    additionalTime?: TimeValue,
    timesheet?: any
}

const props = withDefaults(defineProps<Props>(), {
    fact: 0,
    night: 0,
    nightMain: 0,
    nightAdditional: 0,
    main: 0,
    additional: 0,
    additionalTime: 0,
})

const shifts = computed(() => props.timesheet?.shifts ?? [])
const hasShiftTotals = computed(() => shifts.value.length > 0)
const mainTotalSeconds = computed(() => sumShiftSeconds('main_seconds'))
const additionalTotalSeconds = computed(() => sumShiftSeconds('additional_seconds'))
const nightAdditionalTotalSeconds = computed(() => sumShiftSeconds('additional_night_seconds'))
const nightTotalSeconds = computed(() => sumShiftSeconds('fact_night_seconds'))
const factTotalSeconds = computed(() => sumShiftSeconds('fact_seconds'))

const mainTotal = computed(() => hasShiftTotals.value ? formatSeconds(mainTotalSeconds.value) : props.main)
const additionalTotal = computed(() => hasShiftTotals.value ? formatSeconds(additionalTotalSeconds.value) : props.additional)
const nightMainTotal = computed(() => props.nightMain)
const nightAdditionalTotal = computed(() => hasShiftTotals.value ? formatSeconds(nightAdditionalTotalSeconds.value) : props.nightAdditional)
const nightTotal = computed(() => hasShiftTotals.value ? formatSeconds(nightTotalSeconds.value) : props.night)
const factTotal = computed(() => hasShiftTotals.value ? formatSeconds(factTotalSeconds.value) : props.fact)

const hasMain = computed(() => !isEmptyHours(mainTotal.value))
const hasAdditional = computed(() => !isEmptyHours(additionalTotal.value))

const sortedShifts = computed(() => {
    return [...shifts.value]
        .sort((a: any, b: any) => {
            const startA = getShiftStart(a)
            const startB = getShiftStart(b)

            if (!startA && !startB) return 0
            if (!startA) return 1
            if (!startB) return -1

            return getTimeSortValue(startA) - getTimeSortValue(startB)
        })
})

function sumShiftSeconds(field: string) {
    return shifts.value.reduce((sum: number, shift: any) => sum + (Number(shift?.[field]) || 0), 0)
}

function getShiftPosition(shift: any) {
    return shift.position_name || '—'
}

function getShiftStart(shift: any) {
    return shift.fact_start || shift.plan_start
}

function getShiftEnd(shift: any) {
    return shift.fact_end || shift.plan_end
}

function formatShiftTime(value: string) {
    if (isShortTime(value)) return value.slice(0, 5)

    return dayjs(value).format('HH:mm')
}

function getTimeSortValue(value: string) {
    if (isShortTime(value)) {
        const [hours, minutes] = value.split(':').map(Number)

        return hours * 60 + minutes
    }

    return dayjs(value).valueOf()
}

function isShortTime(value: string) {
    return /^\d{2}:\d{2}/.test(value)
}

function isEmptyHours(value: string | number) {
    if (typeof value === 'number') return value === 0

    const normalizedValue = value.trim()

    return !normalizedValue || normalizedValue === '0' || normalizedValue === '0:00' || normalizedValue === '00:00'
}

function formatSeconds(seconds: number) {
    const safeSeconds = Math.max(0, seconds || 0)
    const hours = Math.floor(safeSeconds / 3600)
    const minutes = Math.floor((safeSeconds % 3600) / 60)

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
}
</script>

<style scoped>
.info-tooltip {
    border: 1px solid rgb(255 255 255 / 0.58);
    background: rgb(255 255 255 / 0.5);
    box-shadow: 0 4px 12px oklch(0% 0 0 / 0.1);
    -webkit-backdrop-filter: blur(8px);
    backdrop-filter: blur(8px);
}
</style>
